import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FiUsers, FiHome, FiDollarSign,
  FiRefreshCw, FiCheck, FiX, FiEdit, FiPlus, FiSmartphone, FiCheckCircle, FiAlertTriangle, FiInfo
} from 'react-icons/fi';
import { superAdminAPI } from '../../services/api';
import StatCard from '../../components/shared/StatCard';
import Modal from '../../components/shared/Modal';

const SuperAdminDashboardPage = () => {
  const [data, setData] = useState(null);
  const [platformPaymentMethods, setPlatformPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingOrderId, setProcessingOrderId] = useState(null);
  const [actionMsg, setActionMsg] = useState(null);

  // Modal Ajustement SMS rapide Agence
  const [adjustModal, setAdjustModal] = useState(null);
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustReason, setAdjustReason] = useState('Attribution Super Admin');
  const [adjusting, setAdjusting] = useState(false);

  // Modal Compte de Paiement Plateforme (Orange Money / Moov Money)
  const [paymentMethodModal, setPaymentMethodModal] = useState(null); // null | 'new' | method object
  const [pmForm, setPmForm] = useState({
    provider: 'orange_money',
    name: 'Orange Money Mali',
    account_type: 'merchant',
    merchant_code: '',
    phone_number: '',
    account_holder_name: 'MaliImmo Plateforme SaaS',
    instructions: '',
    is_active: true,
  });
  const [savingPm, setSavingPm] = useState(false);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const [dashRes, pmRes] = await Promise.all([
        superAdminAPI.getDashboard(),
        superAdminAPI.getPlatformPaymentMethods().catch(() => ({ data: [] })),
      ]);
      setData(dashRes.data);
      setPlatformPaymentMethods(pmRes.data || []);
    } catch (error) {
      console.error('Erreur chargement dashboard super admin:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  const handleProcessOrder = async (order, action) => {
    const masterBalance = Number(data?.sms?.lastKnownBatakiBalance ?? data?.sms?.last_known_balance ?? data?.sms?.master_remaining_credits ?? data?.sms?.remainingCredits ?? 0);

    if (action === 'approve' && masterBalance < order.sms_count) {
      setActionMsg({
        type: 'error',
        text: `Validation impossible : Le compte principal BatakiPlus ne dispose actuellement que de ${masterBalance} SMS disponibles (${order.sms_count} SMS demandés par l'agence "${order.agency_name}"). Vous devez d'abord approvisionner le compte BatakiPlus auprès du fournisseur avant de valider cette commande.`
      });
      return;
    }

    setProcessingOrderId(order.id);
    setActionMsg(null);
    try {
      const res = await superAdminAPI.processSmsOrder(order.id, { action });
      setActionMsg({
        type: 'success',
        text: res.data?.message || (action === 'approve' ? 'Commande validée et crédits SMS attribués !' : 'Commande rejetée.')
      });
      fetchDashboard();
    } catch (err) {
      setActionMsg({
        type: 'error',
        text: err.response?.data?.error || 'Erreur lors du traitement de la commande.'
      });
    } finally {
      setProcessingOrderId(null);
    }
  };

  const handleAdjustSms = async (e) => {
    e.preventDefault();
    if (!adjustModal) return;

    const amount = parseInt(adjustAmount, 10);
    const masterBalance = Number(data?.sms?.lastKnownBatakiBalance ?? data?.sms?.last_known_balance ?? data?.sms?.master_remaining_credits ?? data?.sms?.remainingCredits ?? 0);

    if (amount > 0 && masterBalance < amount) {
      alert(`Attribution impossible : Le compte principal BatakiPlus ne dispose que de ${masterBalance} SMS disponibles (${amount} demandés). Solde insuffisant.`);
      return;
    }

    setAdjusting(true);
    try {
      await superAdminAPI.adjustAgencySms(adjustModal.id, {
        amount,
        reason: adjustReason,
      });
      setActionMsg({
        type: 'success',
        text: `Crédits SMS ajustés avec succès pour ${adjustModal.name} !`
      });
      setAdjustModal(null);
      setAdjustAmount('');
      fetchDashboard();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de l\'ajustement');
    } finally {
      setAdjusting(false);
    }
  };

  const handleOpenPmModal = (method = null) => {
    if (method) {
      setPaymentMethodModal(method);
      setPmForm({
        provider: method.provider || 'orange_money',
        name: method.name || 'Orange Money Mali',
        account_type: method.account_type || 'merchant',
        merchant_code: method.merchant_code || '',
        phone_number: method.phone_number || '',
        account_holder_name: method.account_holder_name || '',
        instructions: method.instructions || '',
        is_active: method.is_active ?? true,
      });
    } else {
      setPaymentMethodModal('new');
      setPmForm({
        provider: 'orange_money',
        name: 'Orange Money Mali',
        account_type: 'merchant',
        merchant_code: '',
        phone_number: '',
        account_holder_name: 'MaliImmo Plateforme SaaS',
        instructions: 'Composer le code USSD ou via l\'application vers le compte marchand ou numéro ci-dessus.',
        is_active: true,
      });
    }
  };

  const handleSavePaymentMethod = async (e) => {
    e.preventDefault();
    if (!pmForm.phone_number.trim() || !pmForm.account_holder_name.trim()) {
      alert('Veuillez renseigner le numéro de téléphone / marchand et le nom du titulaire.');
      return;
    }

    setSavingPm(true);
    try {
      if (paymentMethodModal && paymentMethodModal !== 'new' && paymentMethodModal.id) {
        await superAdminAPI.updatePlatformPaymentMethod(paymentMethodModal.id, pmForm);
        setActionMsg({ type: 'success', text: `Compte ${pmForm.name} mis à jour avec succès !` });
      } else {
        await superAdminAPI.createPlatformPaymentMethod(pmForm);
        setActionMsg({ type: 'success', text: `Nouveau compte ${pmForm.name} ajouté avec succès !` });
      }
      setPaymentMethodModal(null);
      fetchDashboard();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la sauvegarde du compte');
    } finally {
      setSavingPm(false);
    }
  };

  const fmt = (n) => Number(n || 0).toLocaleString('fr-FR');
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  if (loading && !data) {
    return (
      <div className="loading" style={{ padding: 80 }}>
        <div className="spinner" />
        <p style={{ marginTop: 16, color: 'var(--text-secondary)' }}>Chargement du Dashboard Super Admin...</p>
      </div>
    );
  }

  const { agencies = {}, platform = {}, sms = {}, recentAgencies = [], pendingOrders = [], recentSmsLogs = [] } = data || {};
  const masterAvailableBalance = Number(sms.lastKnownBatakiBalance ?? sms.last_known_balance ?? sms.master_remaining_credits ?? sms.remainingCredits ?? 0);

  return (
    <div className="page-container" style={{ paddingBottom: 40 }}>
      {/* Header */}
      <div className="page-header">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ background: '#7c3aed', color: '#fff', padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>
              🛡️ SUPER ADMIN
            </span>
            <h1 style={{ margin: 0, fontSize: 22 }}>Super Administration SaaS MaliImmo</h1>
          </div>
          <p style={{ margin: '4px 0 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>
            Supervision globale, suivi du stock réel BatakiPlus, comptes de paiement et validation des commandes SMS
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-secondary btn-sm" onClick={fetchDashboard}>
            <FiRefreshCw style={{ marginRight: 4 }} /> Actualiser
          </button>
          <Link to="/admin/agencies" className="btn btn-outline btn-sm">
            <FiUsers style={{ marginRight: 4 }} /> Gérer les Agences
          </Link>
        </div>
      </div>

      {/* Action feedback */}
      {actionMsg && (
        <div className="card" style={{
          marginBottom: 20,
          background: actionMsg.type === 'success' ? '#dcfce7' : '#fee2e2',
          borderLeft: `5px solid ${actionMsg.type === 'success' ? '#22c55e' : '#ef4444'}`,
          padding: 14
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <span style={{ fontWeight: 600, color: actionMsg.type === 'success' ? '#15803d' : '#b91c1c', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
              {actionMsg.type === 'success' ? <FiCheckCircle size={18} /> : <FiAlertTriangle size={18} />}
              {actionMsg.text}
            </span>
            <button className="btn btn-sm btn-secondary" onClick={() => setActionMsg(null)}>OK</button>
          </div>
        </div>
      )}

      {/* ── SECTION 1: KPIS GLOBAUX PLATEFORME & SOLDE RÉEL BATAKIPLUS ── */}
      <div className="grid-4" style={{ marginBottom: 24 }}>
        <StatCard
          icon={<FiHome />}
          label="Agences Partenaires"
          value={`${agencies.active || 0} / ${agencies.total || 0}`}
          color="#2563eb"
          bgColor="#eff6ff"
        />
        <StatCard
          icon={<FiUsers />}
          label="Locataires sous Gestion"
          value={fmt(platform.tenants)}
          color="#16a34a"
          bgColor="#f0fdf4"
        />
        <StatCard
          icon={<FiDollarSign />}
          label="Loyers Encaissés Global"
          value={`${fmt(platform.totalRentCollectedXOF)} F`}
          color="#7c3aed"
          bgColor="#faf5ff"
        />
        <div className="card" style={{ padding: '16px 20px', borderRadius: 10, background: masterAvailableBalance > 50 ? '#f0fdf4' : '#fef2f2', border: `1px solid ${masterAvailableBalance > 50 ? '#86efac' : '#fca5a5'}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: masterAvailableBalance > 50 ? '#15803d' : '#b91c1c', textTransform: 'uppercase' }}>
              Solde Réel Passerelle BatakiPlus
            </span>
            <span style={{ fontSize: 10, background: '#e2e8f0', padding: '2px 6px', borderRadius: 4, fontWeight: 600 }}>
              Lecture seule
            </span>
          </div>
          <div style={{ fontSize: 24, fontWeight: 800, color: masterAvailableBalance > 50 ? '#166534' : '#991b1b' }}>
            🪙 {fmt(masterAvailableBalance)} <span style={{ fontSize: 13, fontWeight: 600 }}>SMS restants</span>
          </div>
          <div style={{ fontSize: 11, color: masterAvailableBalance > 50 ? '#15803d' : '#b91c1c', marginTop: 2 }}>
            Total attribués aux agences : <strong>{fmt(sms.allocated_credits || sms.totalAgenciesAllocatedCredits || 0)} SMS</strong>
          </div>
        </div>
      </div>

      {/* ── SECTION 2: COMMANDES SMS EN ATTENTE DE VALIDATION (AVEC VÉRIFICATION STRICTE DU STOCK) ── */}
      {pendingOrders.length > 0 && (
        <div className="card" style={{ marginBottom: 24, borderLeft: '4px solid #f59e0b' }}>
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <div>
              <h2 style={{ fontSize: 16, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="badge badge-warning" style={{ fontWeight: 800 }}>{pendingOrders.length}</span>
                Commandes de Packs SMS en attente de validation
              </h2>
              <p style={{ margin: '2px 0 0 0', fontSize: 12, color: 'var(--text-secondary)' }}>
                Règle Métier : La validation est conditionnée par la disponibilité réelle des SMS sur le compte BatakiPlus.
              </p>
            </div>
            <div style={{ background: '#faf5ff', border: '1px solid #ddd6fe', padding: '6px 12px', borderRadius: 8, fontSize: 12 }}>
              Solde Passerelle actuel : <strong style={{ color: '#7c3aed' }}>{fmt(masterAvailableBalance)} SMS disponibles</strong>
            </div>
          </div>

          <div className="table-container">
            <table className="data-table" style={{ width: '100%', minWidth: 760 }}>
              <thead>
                <tr>
                  <th>Agence</th>
                  <th>Pack Demandé</th>
                  <th>Montant</th>
                  <th>Canal & Numéro Payeur</th>
                  <th>Réf. Transaction</th>
                  <th>Disponibilité BatakiPlus</th>
                  <th style={{ textAlign: 'right' }}>Action Super Admin</th>
                </tr>
              </thead>
              <tbody>
                {pendingOrders.map(order => {
                  const hasEnoughStock = masterAvailableBalance >= order.sms_count;

                  return (
                    <tr key={order.id} style={{ background: hasEnoughStock ? 'transparent' : '#fff5f5' }}>
                      <td>
                        <strong>{order.agency_name}</strong>
                        <div style={{ fontSize: 11, color: '#64748b' }}>{order.agency_phone}</div>
                      </td>
                      <td>
                        <span className="badge badge-info" style={{ fontWeight: 700, fontSize: 11 }}>
                          🪙 {fmt(order.sms_count)} SMS
                        </span>
                      </td>
                      <td>
                        <strong style={{ color: '#0f172a' }}>{fmt(order.price)} XOF</strong>
                      </td>
                      <td>
                        <span style={{ textTransform: 'capitalize', fontWeight: 600, fontSize: 12, color: order.payment_method?.includes('orange') ? '#ea580c' : '#2563eb' }}>
                          {order.payment_method?.replace('_', ' ')}
                        </span>
                        {order.payment_phone && <div style={{ fontSize: 11, color: '#64748b' }}>Payeur : {order.payment_phone}</div>}
                      </td>
                      <td>
                        <code style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: 4, fontWeight: 700, color: '#0f172a', fontSize: 11 }}>
                          {order.payment_reference}
                        </code>
                      </td>
                      <td>
                        {hasEnoughStock ? (
                          <span style={{ color: '#16a34a', fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <FiCheckCircle size={13} /> Solde suffisant ({fmt(masterAvailableBalance)} dispo)
                          </span>
                        ) : (
                          <div>
                            <span style={{ color: '#dc2626', fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <FiAlertTriangle size={13} /> Solde insuffisant ({fmt(masterAvailableBalance)} / {fmt(order.sms_count)} SMS)
                            </span>
                            <div style={{ fontSize: 10, color: '#991b1b', marginTop: 2 }}>
                              Attente de réapprovisionnement réel sur BatakiPlus
                            </div>
                          </div>
                        )}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'nowrap' }}>
                          <button
                            className="btn btn-success btn-sm"
                            disabled={!hasEnoughStock || processingOrderId === order.id}
                            onClick={() => handleProcessOrder(order, 'approve')}
                            title={hasEnoughStock ? "Valider le paiement et créditer les SMS à l'agence" : `Validation bloquée : Le compte BatakiPlus ne dispose que de ${masterAvailableBalance} SMS (${order.sms_count} requis)`}
                            style={{
                              padding: '4px 10px',
                              fontSize: 11,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4,
                              background: hasEnoughStock ? '#16a34a' : '#94a3b8',
                              borderColor: hasEnoughStock ? '#16a34a' : '#94a3b8',
                              cursor: hasEnoughStock ? 'pointer' : 'not-allowed',
                              opacity: hasEnoughStock ? 1 : 0.6
                            }}
                          >
                            <FiCheck /> Valider
                          </button>
                          <button
                            className="btn btn-danger btn-sm"
                            disabled={processingOrderId === order.id}
                            onClick={() => handleProcessOrder(order, 'reject')}
                            title="Rejeter la commande"
                            style={{ padding: '4px 8px', fontSize: 11 }}
                          >
                            <FiX /> Rejeter
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── SECTION 3: COMPTES DE RÉCEPTION DES PAIEMENTS SMS (ORANGE MONEY & MOOV MONEY) ── */}
      <div className="card" style={{ marginBottom: 24, border: '1px solid #cbd5e1' }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div>
            <h2 style={{ fontSize: 16, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
              <FiSmartphone color="#ea580c" />
              Comptes Marchands & Numéros de Réception Super Admin
            </h2>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--text-secondary)' }}>
              Numéros Orange Money et Moov Money communiqués aux agences pour le paiement de leurs commandes SMS
            </p>
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => handleOpenPmModal(null)} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <FiPlus /> Ajouter un Compte
          </button>
        </div>
        <div className="card-body" style={{ padding: '16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }}>
            {platformPaymentMethods.map((pm) => (
              <div
                key={pm.id}
                style={{
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: 10,
                  padding: '14px 16px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  gap: 8
                }}
              >
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <strong style={{ fontSize: 14, color: pm.provider?.includes('orange') ? '#ea580c' : (pm.provider?.includes('moov') ? '#2563eb' : '#059669') }}>
                      📱 {pm.name}
                    </strong>
                    <span className={`badge badge-${pm.is_active ? 'success' : 'secondary'}`} style={{ fontSize: 10 }}>
                      {pm.is_active ? 'Actif' : 'Inactif'}
                    </span>
                  </div>

                  <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', margin: '4px 0' }}>
                    {pm.phone_number} {pm.merchant_code ? `(${pm.merchant_code})` : ''}
                  </div>
                  <div style={{ fontSize: 12, color: '#475569' }}>
                    Titulaire : <strong>{pm.account_holder_name}</strong>
                  </div>
                  {pm.instructions && (
                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 4, fontStyle: 'italic', lineHeight: 1.3 }}>
                      {pm.instructions}
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
                  <button
                    className="btn btn-sm btn-outline"
                    onClick={() => handleOpenPmModal(pm)}
                    style={{ fontSize: 11, padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 4 }}
                  >
                    <FiEdit size={12} /> Modifier le numéro
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── SECTION 4: AGENCES RÉCENTES ET ACTIVITÉ SMS ── */}
      <div className="responsive-grid-2" style={{ gap: 20 }}>
        {/* Agences Récentes */}
        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <h2 style={{ fontSize: 15 }}>Agences Récentes</h2>
            <Link to="/admin/agencies" className="btn btn-outline btn-sm">
              Toutes ({agencies.total || 0})
            </Link>
          </div>
          <div className="table-container">
            <table className="data-table" style={{ width: '100%', minWidth: 500 }}>
              <thead>
                <tr>
                  <th>Agence</th>
                  <th>Locataires</th>
                  <th>Solde SMS</th>
                  <th>Statut</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {recentAgencies.map(ag => (
                  <tr key={ag.id}>
                    <td>
                      <strong style={{ fontSize: 13 }}>{ag.name}</strong>
                      <div style={{ fontSize: 11, color: '#64748b' }}>{ag.city || 'Mali'} • {ag.phone || ag.email}</div>
                    </td>
                    <td>
                      <span className="badge badge-secondary" style={{ fontSize: 11 }}>{ag.tenants_count}</span>
                    </td>
                    <td>
                      <span style={{ fontWeight: 700, fontSize: 11, color: (ag.sms_credits || 0) > 0 ? '#16a34a' : '#ef4444' }}>
                        🪙 {ag.sms_credits || 0}
                      </span>
                    </td>
                    <td>
                      <span className={`badge badge-${ag.is_active ? 'success' : 'danger'}`} style={{ fontSize: 10 }}>
                        {ag.is_active ? 'Active' : 'Inactif'}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline"
                        onClick={() => { setAdjustModal(ag); setAdjustAmount('50'); }}
                        title="Ajuster des crédits SMS"
                        style={{ padding: '3px 8px', fontSize: 11 }}
                      >
                        + SMS
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Logs SMS Récents */}
        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <h2 style={{ fontSize: 15 }}>Derniers SMS Envoyés</h2>
            <Link to="/admin/sms-logs" className="btn btn-outline btn-sm">
              Journal complet
            </Link>
          </div>
          <div className="table-container">
            <table className="data-table" style={{ width: '100%', minWidth: 480 }}>
              <thead>
                <tr>
                  <th>Agence</th>
                  <th>Destinataire</th>
                  <th>Statut</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {recentSmsLogs.map(log => (
                  <tr key={log.id}>
                    <td>
                      <strong style={{ fontSize: 12 }}>{log.agency_name || 'Système'}</strong>
                      <div style={{ fontSize: 10, color: '#64748b' }}>{log.sms_type}</div>
                    </td>
                    <td>
                      <div style={{ fontSize: 12 }}>{log.recipient_name}</div>
                      <div style={{ fontSize: 10, color: '#2563eb' }}>📞 {log.recipient_phone}</div>
                    </td>
                    <td>
                      <span className={`badge badge-${log.status === 'sent' ? 'success' : (log.status === 'simulated' ? 'info' : 'danger')}`} style={{ fontSize: 10 }}>
                        {log.status === 'sent' ? '✅ Délivré' : (log.status === 'simulated' ? '📱 Simulé' : '❌ Échec')}
                      </span>
                    </td>
                    <td style={{ fontSize: 10, color: '#64748b' }}>
                      {fmtDate(log.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* MODAL AJUSTER COMPTE DE PAIEMENT PLATEFORME (Orange Money / Moov Money) */}
      {paymentMethodModal && (
        <Modal
          isOpen={true}
          onClose={() => setPaymentMethodModal(null)}
          title={paymentMethodModal === 'new' ? '➕ Ajouter un Compte Récepteur' : `✏️ Modifier : ${pmForm.name}`}
        >
          <form onSubmit={handleSavePaymentMethod}>
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label style={{ fontWeight: 600, fontSize: 12, marginBottom: 4, display: 'block' }}>Fournisseur *</label>
              <select
                className="form-control"
                value={pmForm.provider}
                onChange={e => {
                  const p = e.target.value;
                  const defaultName = p === 'orange_money' ? 'Orange Money Mali' : (p === 'moov_money' ? 'Moov Money Mali' : 'Wave Mali');
                  setPmForm({ ...pmForm, provider: p, name: defaultName });
                }}
              >
                <option value="orange_money">Orange Money Mali</option>
                <option value="moov_money">Moov Money Mali</option>
                <option value="wave">Wave Mali</option>
                <option value="bank_transfer">Virement Bancaire</option>
              </select>
            </div>

            <div className="form-group" style={{ marginBottom: 12 }}>
              <label style={{ fontWeight: 600, fontSize: 12, marginBottom: 4, display: 'block' }}>Intitulé Affiché *</label>
              <input
                type="text"
                className="form-control"
                value={pmForm.name}
                onChange={e => setPmForm({ ...pmForm, name: e.target.value })}
                required
              />
            </div>

            <div className="form-group" style={{ marginBottom: 12 }}>
              <label style={{ fontWeight: 600, fontSize: 12, marginBottom: 4, display: 'block' }}>
                Numéro de Téléphone Récepteur / Marchand *
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="Ex: +223 78 86 64 73"
                value={pmForm.phone_number}
                onChange={e => setPmForm({ ...pmForm, phone_number: e.target.value })}
                required
              />
            </div>

            <div className="form-group" style={{ marginBottom: 12 }}>
              <label style={{ fontWeight: 600, fontSize: 12, marginBottom: 4, display: 'block' }}>
                Code Marchand (si compte marchand)
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="Ex: CI-788664 ou #144#2#2#..."
                value={pmForm.merchant_code}
                onChange={e => setPmForm({ ...pmForm, merchant_code: e.target.value })}
              />
            </div>

            <div className="form-group" style={{ marginBottom: 12 }}>
              <label style={{ fontWeight: 600, fontSize: 12, marginBottom: 4, display: 'block' }}>
                Nom du Titulaire du Compte *
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="Ex: MaliImmo Plateforme SaaS"
                value={pmForm.account_holder_name}
                onChange={e => setPmForm({ ...pmForm, account_holder_name: e.target.value })}
                required
              />
            </div>

            <div className="form-group" style={{ marginBottom: 16 }}>
              <label style={{ fontWeight: 600, fontSize: 12, marginBottom: 4, display: 'block' }}>
                Instructions affichées aux agences
              </label>
              <textarea
                rows={2}
                className="form-control"
                placeholder="Ex: Composer le #144#2#2# ou via l'application vers le numéro ci-dessus..."
                value={pmForm.instructions}
                onChange={e => setPmForm({ ...pmForm, instructions: e.target.value })}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button type="button" className="btn btn-secondary" onClick={() => setPaymentMethodModal(null)}>
                Annuler
              </button>
              <button type="submit" className="btn btn-primary" disabled={savingPm} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <FiCheckCircle size={15} />
                {savingPm ? 'Enregistrement...' : 'Enregistrer le Compte'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* MODAL AJUSTEMENT RAPIDE DE CRÉDITS SMS AGENCE */}
      {adjustModal && (
        <Modal isOpen={true} onClose={() => setAdjustModal(null)} title={`🪙 Ajuster crédits SMS : ${adjustModal.name}`}>
          <form onSubmit={handleAdjustSms}>
            <div style={{ background: '#f8fafc', padding: 12, borderRadius: 8, marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                <div>
                  <strong>{adjustModal.name}</strong>
                  <div style={{ fontSize: 11, color: '#64748b' }}>{adjustModal.phone || adjustModal.email}</div>
                </div>
                <div>
                  <span style={{ fontSize: 11, color: '#64748b' }}>Solde actuel : </span>
                  <strong style={{ fontSize: 14, color: '#2563eb' }}>{adjustModal.sms_credits || 0} SMS</strong>
                </div>
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: 12 }}>
              <label style={{ fontWeight: 600, fontSize: 13, marginBottom: 4, display: 'block' }}>
                Nombre de SMS à ajouter (ou déduire si négatif) *
              </label>
              <input
                type="number"
                className="form-control"
                required
                placeholder="Ex: 50 ou 100"
                value={adjustAmount}
                onChange={e => setAdjustAmount(e.target.value)}
              />
            </div>

            <div className="form-group" style={{ marginBottom: 16 }}>
              <label style={{ fontWeight: 600, fontSize: 13, marginBottom: 4, display: 'block' }}>
                Motif de l'ajustement
              </label>
              <input
                type="text"
                className="form-control"
                value={adjustReason}
                onChange={e => setAdjustReason(e.target.value)}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button type="button" className="btn btn-secondary" onClick={() => setAdjustModal(null)}>Annuler</button>
              <button type="submit" className="btn btn-primary" disabled={adjusting}>
                {adjusting ? 'Attribution...' : 'Confirmer l\'ajustement'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

export default SuperAdminDashboardPage;
